import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { LoadingView } from '../../../../common/views/LoadingView';
import { ErrorView } from '../../../../common/views/ErrorView/ErrorView';
import { SMLUpgradeFeature } from '../../../config/music24PlanCapabilities';
import { useStreamingAudience } from '../../hooks/useStreamingAudience';
import { useStreamingSearchParam } from '../../hooks/useStreamingSearchParam';
import { pickedOption } from '../../periodSections';
import { sourceLook } from '../../streamingSources';
import { formatMonth } from '../../streamingFormat';
import { audienceMessages as messages } from '../../audienceMessages';
import { streamingPeriodMessages } from '../../streamingPeriodMessages';
import { StreamingPlanLockCard } from '../../parts/StreamingPlanLockCard';
import { SectionSourceSwitch } from '../../period/SectionSourceSwitch';
import { SectionMonthLine } from '../../period/SectionMonthLine';
import { AudienceGenderBlock } from './AudienceGenderBlock';
import { AudienceAgeBlock } from './AudienceAgeBlock';
import { AudienceStoresBlock } from './AudienceStoresBlock';
import { AudienceNoMonth } from './AudienceNoMonth';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import shared from '../../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AudienceBodyProps {
  accountId: number;
  days: number;
}

/** The read of Audience: the source switch, the month line, and the blocks of one month. */
const AudienceBodyComponent: React.FC<AudienceBodyProps> = ({ accountId, days }) => {
  const intl = useIntl();
  const [source, setSource] = useStreamingSearchParam('source');
  const [month, setMonth] = useStreamingSearchParam('month');
  const { data, isLoading, error, retry } = useStreamingAudience({ accountId, days, source, month, skip: false });

  if (isLoading) {
    return <LoadingView />;
  }
  if (error !== undefined) {
    return <ErrorView error={error} retry={retry} />;
  }
  if (data === undefined) {
    return null;
  }
  if (data.locked === 'plan') {
    return (
      <StreamingPlanLockCard
        feature={SMLUpgradeFeature.streamingSplit}
        title={streamingPeriodMessages.planTitle}
        testId="streaming-audience-plan-lock"
      />
    );
  }

  const picked = pickedOption(data.sources, data.source);
  const sourceName = picked === undefined ? '' : sourceLook(picked.integrationType).name;
  const hasData = data.month !== null && (data.gender.length > 0 || data.age.length > 0);

  return (
    <div className={shared.page} data-testid="streaming-section-audience">
      <SectionSourceSwitch options={data.sources} picked={data.source} onPick={setSource} />
      <SectionMonthLine
        text={intl.formatMessage(messages.monthLine, { source: sourceName })}
        shown={
          data.month === null
            ? undefined
            : intl.formatMessage(messages.monthShown, { month: formatMonth(intl, data.month) })
        }
        months={data.months}
        month={data.month}
        onPickMonth={setMonth}
      />
      {!data.available ? (
        <p className={shared.faint}>{intl.formatMessage(streamingPeriodMessages.notAvailable)}</p>
      ) : null}
      {hasData && data.month !== null ? (
        <>
          <div className={shared.blocksTwo}>
            <AudienceGenderBlock gender={data.gender} listeners={data.listeners} />
            <AudienceAgeBlock age={data.age} listeners={data.listeners} />
          </div>
          {data.stores !== null ? <AudienceStoresBlock stores={data.stores} month={data.month} /> : null}
          <p className={shared.footnote} data-testid="streaming-audience-foot">
            {intl.formatMessage(streamingPeriodMessages.reportedBy, {
              source: sourceName,
              period: formatMonth(intl, data.month),
            })}{' '}
            {data.stores === null
              ? intl.formatMessage(messages.noStoreSplit, { source: sourceName })
              : intl.formatMessage(messages.shareNote)}
          </p>
        </>
      ) : (
        <AudienceNoMonth
          answer={data}
          sourceName={sourceName}
          days={days}
          onShowMonth={setMonth}
          onPickSource={setSource}
        />
      )}
    </div>
  );
};

export const AudienceBody = TypedMemo(AudienceBodyComponent);
